import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchPortfolioDetailsReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { PortfolioDetailsReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function portfolioDetailsRowId(row: PortfolioDetailsReportApiItem) {
  return `${row.user_id}-${row.index_fund_id}-${row.subscribed_portfolio_id}`;
}

export function PortfolioDetailsReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'portfolio_id', headerName: 'Portfolio Id', type: 'number', width: 120 },
    { field: 'subscribed_portfolio_id', headerName: 'Subscribed Id', type: 'number', width: 130 },
    { field: 'index_fund_id', headerName: 'Fund Id', type: 'number', width: 100 },
    { field: 'index_fund_name', headerName: 'Fund name', minWidth: 200, flex: 0.5 },
    { field: 'num_of_units', headerName: 'Units', width: 160, valueFormatter: gridFormatDecimalString },
    { field: 'cost_base_per_unit', headerName: 'Cost / unit', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'nav_per_unit', headerName: 'NAV / unit', width: 130, valueFormatter: gridFormatDecimalString },
    { field: 'total_cost_base', headerName: 'Total cost base', width: 160, valueFormatter: gridFormatDecimalString },
    { field: 'mvstartofday', headerName: 'MV start of day', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'mvendofday', headerName: 'MV end of day', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'subscription_date', headerName: 'Subscription date', type: 'dateTime', width: 200 },
    { field: 'is_premium_fund', headerName: 'Premium fund', width: 130, sortable: false, filterable: false },
  ]);

  const { portfolioDetailsReportItems, totalPortfolioDetailsReportItems, fetchPortfolioDetailsReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'portfolio-details-report.csv',
    useCallback(() => reportService.exportPortfolioDetailsReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Portfolio details</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchPortfolioDetailsReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={portfolioDetailsReportItems}
          columns={columns.current}
          getRowId={portfolioDetailsRowId}
          loading={fetchPortfolioDetailsReportStatus === fetchStatuses.pending}
          rowCount={totalPortfolioDetailsReportItems}
          initialSortModel={[{ field: 'subscription_date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
